'use client';
import React, { useCallback, useEffect } from 'react';
import { useSession as useAuthSession, signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { LoadingView } from '@web/components';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { Redirect } from '../../router';
import { useToast } from '../../Toast';

export const LoginFlowContainer = () => {
  const { data: session, status } = useAuthSession();
  const {
    isActive,
    isLead,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();
  const { showToast } = useToast();
  const reporter = useReporter();
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams?.get('next');
  const goToOnboarding = useCallback(() => {
    router.push(`/onboarding/`);
    showToast({
      status: 'info',
      message: 'Complete the quiz and checkout to finish creating your account',
    });
  }, [router, showToast]);

  const isShopifyAuthenticated = session && status === 'authenticated';
  const directToShopifyLogin = !isShopifyAuthenticated;

  let cleanedNext = next?.includes('/account/account') ? '/account/' : next;

  // validate cleanedNext
  if (typeof window !== 'undefined') {
    const ourURL = new URL(window.location.href).hostname;
    const validUrlRegex = new RegExp(
      /^(http(s):\/\/.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
    );
    // checks if the 'next' query param is a valid URL
    const isValidUrl = validUrlRegex.test(cleanedNext);
    // if it's a vlaid URL, but is not within our domain: ignore it so the user logs into their account
    if (isValidUrl && !cleanedNext.includes(ourURL)) cleanedNext = undefined;
  }

  useEffect(() => {
    if (directToShopifyLogin) {
      return signIn('shopify');
    }
  }, [directToShopifyLogin]);

  useEffect(() => {
    if (next?.includes('/account/account')) {
      reporter.error(new Error('next url has value with repeated account'));
    }
  }, [next, reporter]);

  // if the customer is a potential lead that's already
  // authenticated, redirect them back to onboarding
  useEffect(() => {
    if (!isCustomerLoading && !isActive && isLead && isShopifyAuthenticated) {
      goToOnboarding();
    }
  }, [
    isActive,
    isLead,
    isCustomerLoading,
    isShopifyAuthenticated,
    goToOnboarding,
  ]);

  if (isShopifyAuthenticated && isCustomerLoading) {
    return <LoadingView />;
  }

  if (isActive) {
    return <Redirect to={cleanedNext ?? '/account/'} />;
  }

  // auth-time customer fetch failures are recovered in SessionProvider
  // (CustomerUUIDError). only isActive customers redirect to /account.
  return <LoadingView />;
};

LoginFlowContainer.displayName = 'LoginFlowContainer';
